import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, CheckCircle2Icon, ClockIcon, InfoIcon, MapPinIcon, PackageIcon, MessageSquareIcon, CalendarIcon, UsersIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { FulfillmentTransparency, VendorCategory, getCategoryConfig } from '../../data/fulfillmentData';
interface FulfillmentTransparencyCardProps {
  fulfillment: FulfillmentTransparency;
  vendorCategory?: VendorCategory;
}
export const FulfillmentTransparencyCard: React.FC<FulfillmentTransparencyCardProps> = ({
  fulfillment,
  vendorCategory
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const config = getCategoryConfig(vendorCategory || fulfillment.vendorCategory || 'merch');
  const isGreen = fulfillment.delayStatus === 'on-time';
  const isYellow = fulfillment.delayStatus === 'delayed';
  const isRed = fulfillment.delayStatus === 'significant-delay';
  const isActive = fulfillment.communicationStatus === 'active';
  // Determine colors based on status
  const getStatusColors = () => {
    if (isGreen) {
      return dk.isDark ? 'bg-green-900/20 border-green-800 text-green-400' : 'bg-green-50 border-green-200 text-green-700';
    }
    if (isYellow) {
      return dk.isDark ? 'bg-amber-900/20 border-amber-800 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-700';
    }
    return dk.isDark ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-red-50 border-red-200 text-red-700';
  };
  const getStatusIcon = () => {
    if (isGreen) return <CheckCircle2Icon className="w-5 h-5" data-id="element-3950" data-mp="10hd7e7bn6sz1" />;
    if (isYellow) return <ClockIcon className="w-5 h-5" data-id="element-3951" data-mp="10gv4iruh5gi5" />;
    return <AlertTriangleIcon className="w-5 h-5" data-id="element-3952" data-mp="15gpsonmv7rzi" />;
  };
  const getStatusLabel = () => {
    if (isGreen) return config.delayLabels.onTime;
    if (isYellow) return config.delayLabels.delayed;
    return config.delayLabels.significantDelay;
  };
  const getCommunicationColors = () => {
    if (isActive) {
      return dk.isDark ? 'bg-emerald-900/30 text-emerald-400 border-emerald-800/50' : 'bg-emerald-100 text-emerald-800 border-emerald-200';
    }
    return dk.isDark ? 'bg-gray-800 text-gray-400 border-gray-700' : 'bg-gray-100 text-gray-600 border-gray-200';
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Auto-generated summary
  const generateSummary = () => {
    if (config.showQueueFields) {
      if (isGreen) return 'Currently accepting new projects and booking within standard timelines.';
      return `Clients should expect turnaround times to be approximately ${fulfillment.currentCustomerExpectation.replace('Currently booking for ', '')}.`;
    }
    if (isGreen) {
      return 'Orders are currently shipping within standard production timelines.';
    }
    return `Customers should expect orders to ship approximately ${fulfillment.currentCustomerExpectation.replace('Orders currently ship ', '').replace(' later than standard production time', '')} later than standard production timelines.`;
  };
  // Compact version for Green status
  if (isGreen) {
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className={`mb-8 p-4 rounded-xl border flex items-center justify-between ${getStatusColors()}`} data-id="element-3953" data-mp="1kxr0d3go3rge">
        <div className="flex items-center gap-3" data-id="element-3954" data-mp="x2oaqta1hjyv">
          {getStatusIcon()}
          <div data-id="element-3955" data-mp="1207n1nn6fmo6">
            <span className="font-bold mr-2" data-id="element-3956" data-mp="11zxze6pltrln">{getStatusLabel()}</span>
            <span className="text-sm opacity-90" data-id="element-3957" data-mp="2rfxb9vklxv4z">{generateSummary()}</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm opacity-80" data-id="element-3958" data-mp="2weib3tvy6csw">
          {config.showOrigin && <span className="flex items-center gap-1" data-id="element-3959" data-mp="wd0y9li9cg2c">
              <MapPinIcon className="w-4 h-4" data-id="element-3960" data-mp="wbps5f1caw82" /> {fulfillment.shippingOrigin}
            </span>}
          {config.showQueueFields && fulfillment.queueCapacity && <span className="flex items-center gap-1" data-id="element-3961" data-mp="nahw73maq7h5">
              <UsersIcon className="w-4 h-4" data-id="element-3962" data-mp="iczxe61zt8mv" /> {fulfillment.queueCapacity}
            </span>}
        </div>
      </motion.div>;
  }
  // Full card for Yellow/Red status
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className={`mb-8 rounded-xl border overflow-hidden ${dk.card} ${isYellow ? dk.isDark ? 'border-amber-700/50' : 'border-amber-300' : dk.isDark ? 'border-red-700/50' : 'border-red-300'}`} data-id="element-3963" data-mp="8mwwzksjksei">
      {/* Header */}
      <div className={`p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${getStatusColors()}`} data-id="element-3964" data-mp="3qs05zglmpok7">
        <div className="flex items-center gap-3" data-id="element-3965" data-mp="21beb17qa3dxn">
          {getStatusIcon()}
          <h3 className="font-bold text-lg" data-id="element-3966" data-mp="21diclj0pg0ur">
            {config.statusTitle}: {getStatusLabel()}
          </h3>
        </div>

        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getCommunicationColors()}`} data-id="element-3967" data-mp="2b9v6agw92epi">
          <MessageSquareIcon className="w-3.5 h-3.5" data-id="element-3968" data-mp="2fae0ghlm7wuv" />
          {isActive ? 'Actively Communicating' : 'No Recent Updates'}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 sm:p-6" data-id="element-3969" data-mp="1nl2ix8tp1f6i">
        {/* Summary */}
        <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${dk.isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`} data-id="element-3970" data-mp="1rlmggdi6ikt6">
          <InfoIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${dk.textMuted}`} data-id="element-3971" data-mp="1wdcsqp0ofin8" />
          <p className={`text-sm font-medium leading-relaxed ${dk.textPrimary}`} data-id="element-3972" data-mp="ztdn3ihny8kt">
            {generateSummary()}
          </p>
        </div>

        {/* Structured Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3973" data-mp="275w84u4ps8mj">
          <div className="space-y-4" data-id="element-3974" data-mp="2284cpq4v24dl">
            <div data-id="element-3975" data-mp="2271q3uekmo3h">
              <div className={`text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 ${dk.textMuted}`} data-id="element-3976" data-mp="227kwqeyrtq27">
                <PackageIcon className="w-3.5 h-3.5" data-id="element-3977" data-mp="2bxe5z9bfxt3a" />
                {config.primaryTimeLabel}
              </div>
              <div className={`text-sm ${dk.textPrimary}`} data-id="element-3978" data-mp="1o94sqvsamdnr">
                {fulfillment.averageProductionTime}
              </div>
            </div>

            <div data-id="element-3979" data-mp="1jbbhhoc4utgm">
              <div className={`text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 ${dk.textMuted}`} data-id="element-3980" data-mp="1x8hybddaqi37">
                <ClockIcon className="w-3.5 h-3.5" data-id="element-3981" data-mp="1t5v5b94w2r4f" />
                {config.expectationLabel}
              </div>
              <div className={`text-sm ${dk.textPrimary}`} data-id="element-3982" data-mp="38prjq5fzjk75">
                {fulfillment.currentCustomerExpectation}
              </div>
            </div>
          </div>

          <div className="space-y-4" data-id="element-3983" data-mp="3c49jcbx36yfq">
            {config.showOrigin && <div data-id="element-3984" data-mp="3h5yvxvzyjk5x">
                <div className={`text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 ${dk.textMuted}`} data-id="element-3985" data-mp="3h6ifti964iml">
                  <MapPinIcon className="w-3.5 h-3.5" data-id="element-3986" data-mp="37681fubed55u" />
                  {config.originLabel}
                </div>
                <div className={`text-sm ${dk.textPrimary}`} data-id="element-3987" data-mp="3uxdek1pydjcg">
                  {fulfillment.shippingOrigin}
                </div>
              </div>}

            {config.showSubscriptionTiming && fulfillment.subscriptionTiming && <div data-id="element-3988" data-mp="3q5lxqiwlmrzi">
                  <div className={`text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 ${dk.textMuted}`} data-id="element-3989" data-mp="3q4u2j96wyw9r">
                    <CalendarIcon className="w-3.5 h-3.5" data-id="element-3990" data-mp="mgimlhap1jd5" />
                    Subscription Timing
                  </div>
                  <div className={`text-sm ${dk.textPrimary}`} data-id="element-3991" data-mp="2ik6xii33qw3b">
                    {fulfillment.subscriptionTiming}
                  </div>
                </div>}

            {config.showQueueFields && <>
                {fulfillment.queueCapacity && <div data-id="element-3992" data-mp="24sijuad7fzk8">
                    <div className={`text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 ${dk.textMuted}`} data-id="element-3993" data-mp="24t1ud60izlo8">
                      <UsersIcon className="w-3.5 h-3.5" data-id="element-3994" data-mp="2xh7eprno38nj" />
                      Queue Capacity
                    </div>
                    <div className={`text-sm ${dk.textPrimary}`} data-id="element-3995" data-mp="2sh1sir062ecu">
                      {fulfillment.queueCapacity}
                    </div>
                  </div>}
                {fulfillment.bookingWindow && <div data-id="element-3996" data-mp="1h490rg7dlpws">
                    <div className={`text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 ${dk.textMuted}`} data-id="element-3997" data-mp="1h0bjl04cz8fj">
                      <CalendarIcon className="w-3.5 h-3.5" data-id="element-3998" data-mp="121hfqpejqmh3" />
                      Booking Window
                    </div>
                    <div className={`text-sm ${dk.textPrimary}`} data-id="element-3999" data-mp="16wekvl8pgbop">
                      {fulfillment.bookingWindow}
                    </div>
                  </div>}
              </>}
          </div>
        </div>

        {/* Footer */}
        <div className={`mt-6 pt-4 border-t flex items-center justify-between text-xs ${dk.border} ${dk.textMuted}`} data-id="element-4000" data-mp="1kpcp7x34le4e">
          <div data-id="element-4001" data-mp="1pr1wr2r539nq">Last updated: {formatDate(fulfillment.lastUpdated)}</div>
          {!isActive && <div className="italic" data-id="element-4002" data-mp="1umh7vmxrirgg">
              No updates posted in the last {fulfillment.daysSinceLastUpdate}{' '}
              days
            </div>}
        </div>
      </div>
    </motion.div>;
};